import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full pt-24 sm:pt-32 lg:pt-40">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-6 sm:mb-8 tracking-tight leading-tight">
            When plans fall apart,<br />you don't.
          </h1>
          <p className="text-base sm:text-lg lg:text-2xl text-gray-700 mb-4 sm:mb-6 font-normal leading-relaxed max-w-4xl mx-auto">
            Halvera is a private continuity system you rely on when travel, schedules, or logistics go sideways — so you're not scrambling, rebooking, or solving problems under pressure.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-8 sm:mb-12 font-normal max-w-3xl mx-auto">
            Most people absorb disruption themselves. Halvera members hand it off — and keep moving.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Link 
              to="/how-it-works" 
              className="w-full sm:w-auto px-7 sm:px-9 py-3 sm:py-4 bg-red-600 text-white rounded-md text-sm sm:text-base font-semibold hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer text-center"
            >
              See How It Works
            </Link>
            <Link 
              to="/apply" 
              className="w-full sm:w-auto px-7 sm:px-9 py-3 sm:py-4 bg-white text-gray-900 border-2 border-gray-300 rounded-md text-sm sm:text-base font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-center"
            >
              Apply for Access
            </Link>
          </div>
        </div>
      </section>

      {/* When Halvera Shows Up */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-12 lg:mb-16 text-center">
            When Halvera Shows Up
          </h2>
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            <div>
              <ul className="space-y-4 sm:space-y-5">
                <li className="flex items-start gap-3 sm:gap-4">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                  <span className="text-base sm:text-lg text-gray-700 font-normal">Flights are canceled.</span>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                  <span className="text-base sm:text-lg text-gray-700 font-normal">A meeting moves cities overnight.</span>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                  <span className="text-base sm:text-lg text-gray-700 font-normal">A document is missing.</span>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                  <span className="text-base sm:text-lg text-gray-700 font-normal">A medical issue comes up abroad.</span>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                  <span className="text-base sm:text-lg text-gray-700 font-normal">A trip becomes complicated instead of simple.</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-6 sm:p-8 lg:p-10 rounded-lg">
              <div className="space-y-4 sm:space-y-6">
                <p className="text-base sm:text-lg text-gray-300 font-normal leading-relaxed">
                  Most people start calling airlines, hotels, assistants, insurers, and coworkers.
                </p>
                <p className="text-lg sm:text-xl text-white font-semibold">
                  Halvera members don't.
                </p>
                <p className="text-base sm:text-lg text-gray-300 font-normal leading-relaxed">
                  They pass it to one system — and their day keeps going.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Halvera Is */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-xs sm:text-sm font-semibold text-red-600 mb-3 sm:mb-4 uppercase tracking-wide">
            What Halvera Is
          </h2>
          <h3 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
            A private continuity system.
          </h3>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-4 sm:mb-6 font-normal leading-relaxed">
            Halvera replaces fragmented services and reactive problem-solving with a single place to turn when things stop going to plan.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 font-normal">
            It's not about luxury or perks. It's about relief.
          </p>
        </div>
      </section>

      {/* Who Halvera Is For */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-12 lg:mb-16 text-center">
            Who Halvera Is For
          </h2>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            <div className="bg-white border-2 border-gray-200 p-6 sm:p-8 lg:p-10 rounded-lg hover:border-red-600 transition-all">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Individuals</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-6 sm:mb-8 font-normal leading-relaxed">
                For people who move often, think ahead, and don't want disruption stealing their attention.
              </p>
              <Link 
                to="/for-individuals" 
                className="inline-flex items-center text-red-600 font-semibold text-sm hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                For Individuals
                <i className="ri-arrow-right-line ml-2 text-lg"></i>
              </Link>
            </div>
            <div className="bg-white border-2 border-gray-200 p-6 sm:p-8 lg:p-10 rounded-lg hover:border-red-600 transition-all">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Companies</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-2 sm:mb-3 font-normal leading-relaxed">
                For organizations supporting people whose time, presence, and reliability materially affect outcomes.
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-6 sm:mb-8 font-normal leading-relaxed">
                Halvera keeps costs predictable and administrative overhead minimal.
              </p>
              <Link 
                to="/for-companies" 
                className="inline-flex items-center text-red-600 font-semibold text-sm hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                For Companies
                <i className="ri-arrow-right-line ml-2 text-lg"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Halvera Quietly Handles */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-12 lg:mb-16 text-center">
            What Halvera Quietly Handles
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                <i className="ri-route-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Coordination</h3>
              <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                Changes, rerouting, and resolution handled through one place — without escalation.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                <i className="ri-key-2-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Access</h3>
              <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                Priority pathways and trusted services when standard options fall short.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                <i className="ri-shield-check-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Protection</h3>
              <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                Medical, legal, documentation, and logistical support during disruption.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                <i className="ri-refresh-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Continuity</h3>
              <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                No restarting. No re-explaining. One system that follows you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Halvera Is Built To Handle */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
            What Halvera Is Built To Handle
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-6 sm:mb-8 text-center font-normal leading-relaxed">
            Halvera supports the full surface area of modern movement — without being defined by any single category.
          </p>
          <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 max-w-2xl mx-auto">
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">travel and accommodations</span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">routing, access, and destinations</span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">workspaces and temporary bases</span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">documentation, compliance, and logistics</span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">medical, legal, and emergency situations</span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></span>
              <span className="text-sm sm:text-base text-gray-700">experiences, reservations, and time-sensitive needs</span>
            </li>
          </ul>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 text-center font-normal leading-relaxed">
            They're simply part of what the system can handle — so members don't have to.
          </p>
          <p className="text-lg sm:text-xl font-semibold text-gray-900 text-center">
            The difference isn't what's available. It's who stays responsible when things change.
          </p>
        </div>
      </section>

      {/* Access Levels Preview */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-5">
              Access Levels
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-normal max-w-3xl mx-auto">
              How much responsibility Halvera assumes — not how much you travel.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="bg-white p-6 sm:p-8 rounded-lg border-2 border-gray-200 hover:border-red-600 transition-all">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Core</h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal">Life runs properly</p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border-2 border-gray-200 hover:border-red-600 transition-all">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Reserve</h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal">You're prioritized when things break</p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border-2 border-gray-200 hover:border-red-600 transition-all sm:col-span-2 md:col-span-1">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Ascend</h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal">You don't manage logistics at all</p>
            </div>
          </div>
          <div className="text-center">
            <Link 
              to="/access-levels" 
              className="inline-flex items-center px-7 sm:px-8 py-3 sm:py-4 bg-red-600 text-white rounded-md text-sm sm:text-base font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
            >
              Explore Access Levels
              <i className="ri-arrow-right-line ml-2 text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Gate Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4 sm:mb-6">
            Halvera access is intentionally limited.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-10 font-normal leading-relaxed">
            This isn't a product you browse. It's a system you're granted access to.
          </p>
          <Link 
            to="/apply" 
            className="inline-flex items-center px-8 sm:px-10 py-3 sm:py-4 bg-red-600 text-white rounded-md text-sm sm:text-base font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
          >
            Apply for Access
            <i className="ri-arrow-right-line ml-2 text-lg"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
